'use server'

import { apiRequest, ApiResponse } from '../request'

export type PainLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export interface VitalSign {
    id: number
    companyId: number
    hospitalId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
        bed?: {
            id: number
            number: string
        }
        unit?: {
            id: number
            name: string
        }
    }
    temperature?: number
    heartRate?: number
    respiratoryRate?: number
    systolicBP?: number
    diastolicBP?: number
    oxygenSaturation?: number
    bloodGlucose?: number
    painLevel?: PainLevel
    consciousnessLevel?: string
    glasgowScore?: number
    glasgowEyeResponse?: number
    glasgowVerbalResponse?: number
    glasgowMotorResponse?: number
    pupilRight?: string
    pupilLeft?: string
    capillaryRefill?: string
    skinColor?: string
    edema?: string
    notes?: string
    measuredAt: string
    measuredById?: number
    measuredBy?: {
        id: number
        name: string
    }
    createdAt: string
    updatedAt: string
}

export interface CreateVitalSignData {
    admissionId: number
    temperature?: number
    heartRate?: number
    respiratoryRate?: number
    systolicBP?: number
    diastolicBP?: number
    oxygenSaturation?: number
    bloodGlucose?: number
    painLevel?: PainLevel
    consciousnessLevel?: string
    glasgowScore?: number
    glasgowEyeResponse?: number
    glasgowVerbalResponse?: number
    glasgowMotorResponse?: number
    pupilRight?: string
    pupilLeft?: string
    capillaryRefill?: string
    skinColor?: string
    edema?: string
    notes?: string
    measuredAt?: string
}

export interface UpdateVitalSignData extends Partial<CreateVitalSignData> {}

export interface VitalSignFilters {
    hospitalId?: number
    admissionId?: number
    startDate?: string
    endDate?: string
}

export interface VitalSignsAlert {
    id: number
    companyId: number
    vitalSignId: number
    admissionId: number
    admission?: {
        id: number
        client: {
            id: number
            name: string
        }
    }
    parameter: string
    value: number
    severity: 'WARNING' | 'CRITICAL'
    message: string
    acknowledgedAt?: string
    acknowledgedById?: number
    acknowledgedBy?: {
        id: number
        name: string
    }
    createdAt: string
}

export async function getVitalSigns(
    companyId?: number,
    filters?: VitalSignFilters
): Promise<ApiResponse<VitalSign[]>> {
    return apiRequest<VitalSign[]>('hospital/vital-signs', {
        params: { companyId, ...filters },
    })
}

export async function getVitalSign(id: number): Promise<ApiResponse<VitalSign>> {
    return apiRequest<VitalSign>(`hospital/vital-signs/${id}`, {})
}

export async function getAdmissionVitalSigns(
    admissionId: number,
    limit?: number
): Promise<ApiResponse<VitalSign[]>> {
    return apiRequest<VitalSign[]>(`hospital/vital-signs/admission/${admissionId}`, {
        params: { limit },
    })
}

export async function getLatestVitalSigns(
    admissionId: number
): Promise<ApiResponse<VitalSign | null>> {
    return apiRequest<VitalSign | null>(
        `hospital/vital-signs/latest/${admissionId}`,
        {}
    )
}

export async function createVitalSign(
    data: CreateVitalSignData
): Promise<ApiResponse<VitalSign>> {
    return apiRequest<VitalSign>('hospital/vital-signs', { data }, 'post')
}

export async function updateVitalSign(
    id: number,
    data: UpdateVitalSignData
): Promise<ApiResponse<VitalSign>> {
    return apiRequest<VitalSign>(`hospital/vital-signs/${id}`, { data }, 'patch')
}

export async function deleteVitalSign(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/vital-signs/${id}`, {}, 'delete')
}

export async function getVitalSignsAlerts(
    companyId: number
): Promise<ApiResponse<VitalSignsAlert[]>> {
    return apiRequest<VitalSignsAlert[]>(
        `hospital/vital-signs/alerts/${companyId}`,
        {}
    )
}

export async function acknowledgeAlert(
    alertId: number
): Promise<ApiResponse<VitalSignsAlert>> {
    return apiRequest<VitalSignsAlert>(
        `hospital/vital-signs/alerts/${alertId}/acknowledge`,
        {},
        'patch'
    )
}

export const PAIN_LEVEL_LABELS: Record<PainLevel, string> = {
    0: 'Sem dor',
    1: 'Mínima',
    2: 'Leve',
    3: 'Leve',
    4: 'Moderada',
    5: 'Moderada',
    6: 'Moderada',
    7: 'Intensa',
    8: 'Intensa',
    9: 'Muito intensa',
    10: 'Insuportável',
}

export const PAIN_LEVEL_COLORS: Record<PainLevel, string> = {
    0: 'bg-green-500',
    1: 'bg-green-400',
    2: 'bg-lime-400',
    3: 'bg-lime-500',
    4: 'bg-yellow-400',
    5: 'bg-yellow-500',
    6: 'bg-orange-400',
    7: 'bg-orange-500',
    8: 'bg-red-400',
    9: 'bg-red-500',
    10: 'bg-red-700',
}
